import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Sparkles, CreditCard } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { WALLET_TOPUP_OPTIONS, formatPrice, getWalletTopupDetails } from '@/config/pricing';
import { logger } from '@/utils/logger';

interface WalletTopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (amount: number, totalWithBonus: number) => Promise<void>;
  currentBalance: number;
}

export const WalletTopUpModal: React.FC<WalletTopUpModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentBalance,
}) => {
  const [selectedAmount, setSelectedAmount] = useState<number>(10);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleConfirm = async () => {
    if (isProcessing) return;

    try {
      setIsProcessing(true);
      const details = getWalletTopupDetails(selectedAmount);
      await onConfirm(selectedAmount, details.total);
      onClose();
    } catch (error) {
      logger.error('Wallet top-up failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const selectedDetails = getWalletTopupDetails(selectedAmount);
  const newBalance = currentBalance + selectedDetails.total;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-900">
            Add Funds to Wallet
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Amount Selection */}
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-3 block">
              Select Amount
            </label>
            <div className="grid grid-cols-5 gap-3">
              {WALLET_TOPUP_OPTIONS.map((option) => {
                const isSelected = selectedAmount === option.amount;
                const hasBonus = option.bonus > 0;

                return (
                  <motion.button
                    key={option.amount}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedAmount(option.amount)}
                    className={`
                      relative rounded-lg p-4 border-2 transition-all
                      ${isSelected
                        ? 'border-collectability-blue bg-collectability-blue-light shadow-md'
                        : 'border-slate-200 bg-white hover:border-collectability-blue/50'
                      }
                      ${hasBonus ? 'ring-2 ring-green-500/30' : ''}
                    `}
                  >
                    {/* Bonus Badge */}
                    {hasBonus && (
                      <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        {Math.round((option.bonus / option.amount) * 100)}%
                      </div>
                    )}

                    {/* Check Mark */}
                    <AnimatePresence>
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 0 }}
                          className="absolute top-2 left-2 w-6 h-6 rounded-full bg-collectability-blue flex items-center justify-center"
                        >
                          <Check className="w-4 h-4 text-white" />
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Amount */}
                    <div className={`text-center ${isSelected ? 'mt-2' : ''}`}>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatPrice(option.amount)}
                      </p>
                      {hasBonus && (
                        <p className="text-xs text-green-600 font-semibold mt-1">
                          +{formatPrice(option.bonus)}
                        </p>
                      )}
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Top-up amount</span>
              <span className="font-semibold text-slate-900">
                {formatPrice(selectedAmount)}
              </span>
            </div>
            {selectedDetails.bonus > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-green-600 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Bonus credit
                </span>
                <span className="font-semibold text-green-600">
                  +{formatPrice(selectedDetails.bonus)}
                </span>
              </div>
            )}
            <div className="flex justify-between text-sm pt-2 border-t border-slate-200">
              <span className="text-slate-600">Total to add</span>
              <span className="font-bold text-collectability-blue">
                {formatPrice(selectedDetails.total)}
              </span>
            </div>
            <div className="flex justify-between text-sm pt-2 border-t border-slate-200">
              <span className="text-slate-700 font-medium">New balance</span>
              <span className="font-bold text-slate-900">
                {formatPrice(newBalance)}
              </span>
            </div>
          </div>

          {/* Usage Examples */}
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm font-semibold text-blue-900 mb-2">
              With {formatPrice(selectedDetails.total)}, you can perform:
            </p>
            <div className="grid grid-cols-2 gap-3 text-xs text-blue-700">
              <div>
                <span className="font-semibold">{Math.floor(selectedDetails.total / 0.05)}</span> enrichment searches
              </div>
              <div>
                <span className="font-semibold">{Math.floor(selectedDetails.total / 0.25)}</span> skip trace searches
              </div>
              <div>
                <span className="font-semibold">{Math.floor(selectedDetails.total / 0.65)}</span> AI scoring searches
              </div>
              <div>
                <span className="font-semibold">{Math.floor(selectedDetails.total / 0.70)}</span> full intelligence reports
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isProcessing}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isProcessing}
              className="flex-1 bg-collectability-blue text-white hover:bg-blue-700"
            >
              {isProcessing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Add {formatPrice(selectedAmount)}
                </>
              )}
            </Button>
          </div>

          {/* Terms */}
          <p className="text-xs text-slate-500 text-center">
            Funds never expire and can be used for any search type. Payment secured by Stripe.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
